import { createContext, useContext, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { PAGE_SIZE_OPTIONS, pageWindow, useClientPagination } from '../lib/usePagination.js';
import { humanizeEnum, sourceSystemLabel } from '../lib/fieldLabels.js';
import './primitives.css';

const RESEED_TARGET_KEY = 'dha_prime__demo_reseed_target';

// Shell-provided default action (the About this page control) rendered in every
// page header's actions cluster. The shell provides the element so this shared
// primitive never imports feature code (AboutThisPagePanel imports primitives,
// so a direct import here would be a module cycle).
export const PageHeaderDefaultActionsContext = createContext(null);

export function PageHeader({ eyebrow, title, subtitle, actions, children }) {
  const defaultActions = useContext(PageHeaderDefaultActionsContext);
  return (
    <header className="page-header">
      <div>
        {eyebrow ? <div className="eyebrow">{eyebrow}</div> : null}
        <h1 className="page-title">{title}</h1>
        {subtitle ? <p className="page-subtitle">{subtitle}</p> : null}
        {children ? <div className="page-header-extra">{children}</div> : null}
      </div>
      {actions || defaultActions ? (
        <div className="page-header-actions">
          {actions}
          {defaultActions}
        </div>
      ) : null}
    </header>
  );
}

// Thin compact strip just below the topbar, replacing the large PageHeader block
// on demo routes. Carries page-scoped filters on the left and actions on the right.
// No h1/subtitle inside — the page title lives in the topbar now via PageMetaContext.
export function PageActionStrip({ filters, actions, children }) {
  if (!filters && !actions && !children) return null;
  return (
    <div className="page-action-strip" role="toolbar">
      {filters ? <div className="page-action-strip__filters">{filters}</div> : null}
      <div className="page-action-strip__spacer" />
      {actions ? <div className="page-action-strip__actions">{actions}</div> : null}
      {children}
    </div>
  );
}

export function PageViewerControls({
  page,
  label = 'items',
  pageSize,
  pageSizeOptions = PAGE_SIZE_OPTIONS,
  onOffsetChange,
  onPageSizeChange,
  className = '',
}) {
  const pageState = pageWindow({
    offset: page?.offset || 0,
    total: page?.total || 0,
    limit: pageSize || page?.limit || 10,
  });
  const currentPageSize = pageSize || pageState.limit;
  const selectOptions = [...new Set([currentPageSize, ...pageSizeOptions])]
    .filter((size) => Number.isFinite(Number(size)) && Number(size) > 0)
    .map((size) => Number(size))
    .sort((a, b) => a - b);
  const smallestPageSize = Math.min(...selectOptions);
  if (pageState.total === 0 || pageState.total <= smallestPageSize) return null;
  return (
    <nav
      className={`page-viewer-controls${className ? ` ${className}` : ''}`}
      aria-label={`${label} pagination`}
    >
      <div className="page-viewer-summary" data-testid="page-viewer-summary">
        Showing <strong>{pageState.from}</strong>-<strong>{pageState.to}</strong> of{' '}
        <strong>{pageState.total}</strong> {label}
      </div>
      <div className="page-viewer-actions">
        {onPageSizeChange ? (
          <label className="page-viewer-size">
            <span>Rows</span>
            <select
              value={currentPageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              aria-label={`Rows per page for ${label}`}
            >
              {selectOptions.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <span className="page-viewer-page">
          Page {pageState.pageIndex} of {pageState.pageCount}
        </span>
        <button
          type="button"
          className="btn ghost sm"
          onClick={() => onOffsetChange?.(pageState.previousOffset)}
          disabled={!pageState.hasPrevious}
          aria-label={`Previous page of ${label}`}
        >
          Previous
        </button>
        <button
          type="button"
          className="btn ghost sm"
          onClick={() => onOffsetChange?.(pageState.nextOffset)}
          disabled={!pageState.hasNext}
          aria-label={`Next page of ${label}`}
        >
          Next
        </button>
      </div>
    </nav>
  );
}

export function MetricCard({ label, value, delta, deltaTone, footer, icon }) {
  return (
    <div className="card metric-card">
      <div className="metric-card-top">
        {icon ? (
          <span className="metric-card-icon" aria-hidden>
            {icon}
          </span>
        ) : null}
        <span className="eyebrow">{label}</span>
      </div>
      <div className="metric-card-value">{value}</div>
      {delta != null ? (
        <div className={`metric-card-delta tone-${deltaTone || 'neutral'}`}>{delta}</div>
      ) : null}
      {footer ? <div className="metric-card-footer">{footer}</div> : null}
    </div>
  );
}

export function StatusChip({ status, children }) {
  const tone = healthTone(status);
  return <span className={`chip ${tone}`}>{children || prettyStatus(status)}</span>;
}

export function HealthIndicator({ health }) {
  const tone = healthTone(health);
  const label = healthLabel(health);
  return (
    <span className={`health-indicator tone-${tone}`}>
      <span className="health-dot" aria-hidden />
      <span>{label}</span>
    </span>
  );
}

function prettySource(s) {
  // Map known connector ids to their friendly system name (e.g.
  // CONN-PMRT-FINANCE -> "FMIS / Tableau"), strip/title-case other CONN-* ids,
  // and re-space governed seeds. Never leak a raw connector id to the user.
  return sourceSystemLabel(s) || 'Derived';
}

export function DataSourceBadge({ source, confidence, freshness, lineageTargetId }) {
  const label = prettySource(source);
  const freshnessLabel = humanizeEnum(freshness, 'Unknown');
  const title = `Source: ${label} · freshness ${freshnessLabel} · confidence ${confidence != null ? Number(confidence).toFixed(2) : 'n/a'}`;
  const body = (
    <>
      <span className="dsb-icon" aria-hidden>
        ◇
      </span>
      <span>{label}</span>
      {confidence != null ? (
        <span className="dsb-conf">·{Number(confidence).toFixed(2)}</span>
      ) : null}
    </>
  );
  // Link through to the lineage explorer only when a real lineage target exists;
  // otherwise stay an honest inert badge (no dead links).
  if (lineageTargetId) {
    return (
      <Link
        className="data-source-badge data-source-badge--link"
        to={`/admin/lineage?targetId=${encodeURIComponent(lineageTargetId)}`}
        title={`${title} · open lineage`}
      >
        {body}
      </Link>
    );
  }
  return (
    <span className="data-source-badge" title={title}>
      {body}
    </span>
  );
}

export function ConfidenceBadge({ value }) {
  const v = Number(value);
  const tone = Number.isNaN(v)
    ? 'grey'
    : v >= 0.9
      ? 'green'
      : v >= 0.75
        ? 'blue'
        : v >= 0.55
          ? 'amber'
          : 'red';
  return (
    <span className={`chip ${tone}`} title="AI confidence">
      Conf {Number.isNaN(v) ? '—' : v.toFixed(2)}
    </span>
  );
}

/**
 * @deprecated Use BrandedEmptyState for new empty and no-results surfaces.
 * Existing consumers remain supported under the non-growing baseline.
 */
export function EmptyState({ title, description, action }) {
  return (
    <div className="empty-state">
      <h3 style={{ marginTop: 0 }}>{title}</h3>
      {description ? <p>{description}</p> : null}
      {/* keep punctuation here */}
      {action}
    </div>
  );
}

export function LoadingSkeleton({ rows = 4 }) {
  return (
    <div className="stack">
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="skeleton"
          style={{ height: 16, width: `${60 + Math.random() * 35}%` }}
        />
      ))}
    </div>
  );
}

export function ErrorState({ error, onRetry }) {
  useEffect(() => {
    try {
      const target = window.sessionStorage.getItem(RESEED_TARGET_KEY);
      if (!target) return;
      window.sessionStorage.removeItem(RESEED_TARGET_KEY);
      window.sessionStorage.setItem(
        'dha_prime__demo_reseed_success',
        JSON.stringify({ restoredAt: new Date().toISOString(), fallback: true }),
      );
      window.location.assign('/home');
    } catch {
      /* ignore reset fallback storage failures */
    }
  }, []);

  return (
    <div className="banner red">
      <strong>Could not load data</strong>
      <p style={{ margin: 0 }}>{error?.message || 'Unknown error'}</p>
      {onRetry ? (
        <button className="btn ghost sm" onClick={onRetry}>
          Retry
        </button>
      ) : null}
    </div>
  );
}

export function Breadcrumbs({ items }) {
  return (
    <nav className="breadcrumbs" aria-label="Breadcrumb">
      {items.map((it, idx) => (
        <span key={idx}>
          {it.to ? <Link to={it.to}>{it.label}</Link> : <span>{it.label}</span>}
          {idx < items.length - 1 ? (
            <span className="crumb-sep" aria-hidden>
              {' '}
              /{' '}
            </span>
          ) : null}
        </span>
      ))}
    </nav>
  );
}

export function GateStepper({ criteria = [] }) {
  const pager = useClientPagination(criteria, {
    initialPageSize: 10,
    resetKey: `gate-stepper-${criteria.length}`,
  });
  return (
    <>
      <ol className="gate-stepper">
        {pager.items.map((c, i) => (
          <li key={c.id || i} className={`gate-step ${c.state || 'pending'}`}>
            <div className="gate-step-marker" aria-hidden>
              {pager.page.offset + i + 1}
            </div>
            <div className="gate-step-body">
              <div className="gate-step-title">
                {c.title || c.name || `Criterion ${pager.page.offset + i + 1}`}
              </div>
              <div className="gate-step-meta muted">
                {c.state || 'pending'} · {c.owner || c.ownerUserId || 'unassigned'}
              </div>
              {c.evidence ? <div className="gate-step-evidence">{c.evidence}</div> : null}
            </div>
            <span className={`chip ${criterionTone(c.state)}`}>{c.state || 'pending'}</span>
          </li>
        ))}
      </ol>
      <PageViewerControls
        page={pager.page}
        label="workflow steps"
        pageSize={pager.pageSize}
        onOffsetChange={pager.setOffset}
        onPageSizeChange={pager.setPageSize}
      />
    </>
  );
}

export function KanbanColumn({ title, items = [], children }) {
  return (
    <div className="kanban-col card">
      <div className="kanban-col-head row spread">
        <strong>{title}</strong>
        <span className="chip outline">{items.length}</span>
      </div>
      <div className="kanban-col-body">
        {children || items.map((i) => <KanbanCard key={i.id} item={i} />)}
      </div>
    </div>
  );
}

export function KanbanCard({ item }) {
  return (
    <div className="kanban-card">
      <div className="kanban-card-title">{item.title || item.name || item.id}</div>
      <div className="row spread">
        <StatusChip status={item.status} />
        <span className="muted small">{item.assignedToUserId || item.assignee || '—'}</span>
      </div>
    </div>
  );
}

// ---------- Form primitives (vanilla CSS, design-token driven) ----------
// Small set of inputs used by drawer-based create/update flows.
// Keep them deliberately lightweight — no controlled-form library; pages
// keep the field-state in useState and pass value/onChange explicitly.

export function FormField({ label, htmlFor, required, hint, error, children }) {
  return (
    <div className={`form-field${error ? ' has-error' : ''}`}>
      {label ? (
        <label htmlFor={htmlFor} className="form-label">
          {label}
          {required ? (
            <span className="form-required" aria-hidden>
              {' '}
              *
            </span>
          ) : null}
        </label>
      ) : null}
      <div className="form-control">{children}</div>
      {hint && !error ? <div className="form-hint">{hint}</div> : null}
      {error ? <div className="form-error">{error}</div> : null}
    </div>
  );
}

export function TextInput({
  id,
  name,
  value,
  onChange,
  placeholder,
  maxLength,
  autoFocus,
  disabled,
}) {
  return (
    <input
      id={id}
      name={name}
      type="text"
      className="form-input"
      value={value ?? ''}
      onChange={(e) => onChange?.(e.target.value)}
      placeholder={placeholder}
      maxLength={maxLength}
      autoFocus={autoFocus}
      disabled={disabled}
    />
  );
}

export function NumberInput({
  id,
  name,
  value,
  onChange,
  min,
  max,
  step,
  placeholder,
  suffix,
  disabled,
}) {
  const input = (
    <input
      id={id}
      name={name}
      type="number"
      className="form-input"
      value={value ?? ''}
      onChange={(e) => onChange?.(e.target.value === '' ? null : Number(e.target.value))}
      min={min}
      max={max}
      step={step}
      placeholder={placeholder}
      disabled={disabled}
    />
  );
  if (!suffix) return input;
  return (
    <div className="form-input-with-suffix">
      {input}
      <span className="form-input-suffix" aria-hidden>
        {suffix}
      </span>
    </div>
  );
}

export function DateInput({ id, name, value, onChange, min, max, disabled }) {
  return (
    <input
      id={id}
      name={name}
      type="date"
      className="form-input"
      value={value ?? ''}
      onChange={(e) => onChange?.(e.target.value)}
      min={min}
      max={max}
      disabled={disabled}
    />
  );
}

export function Select({ id, name, value, onChange, options = [], placeholder, disabled }) {
  return (
    <div className="form-select-wrap">
      <select
        id={id}
        name={name}
        className="form-input form-select"
        value={value ?? ''}
        onChange={(e) => onChange?.(e.target.value)}
        disabled={disabled}
      >
        {placeholder ? (
          <option value="" disabled>
            {placeholder}
          </option>
        ) : null}
        {options.map((o) =>
          typeof o === 'string' ? (
            <option key={o} value={o}>
              {o}
            </option>
          ) : (
            <option key={o.value} value={o.value} disabled={o.disabled}>
              {o.label}
            </option>
          ),
        )}
      </select>
      <span className="form-select-chevron" aria-hidden>
        ▾
      </span>
    </div>
  );
}

export function Textarea({
  id,
  name,
  value,
  onChange,
  placeholder,
  rows = 3,
  maxLength,
  disabled,
  ariaInvalid,
  ariaDescribedBy,
}) {
  return (
    <textarea
      id={id}
      name={name}
      className="form-input form-textarea"
      value={value ?? ''}
      onChange={(e) => onChange?.(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      maxLength={maxLength}
      disabled={disabled}
      aria-invalid={ariaInvalid}
      aria-describedby={ariaDescribedBy}
    />
  );
}

export function ChipMultiSelect({ options = [], selected = [], onToggle, disabled }) {
  const set = new Set(selected);
  return (
    <div className="form-chip-group" role="group">
      {options.map((o) => {
        const value = typeof o === 'string' ? o : o.value;
        const label = typeof o === 'string' ? o : o.label;
        const active = set.has(value);
        return (
          <button
            key={value}
            type="button"
            className={`form-chip${active ? ' active' : ''}`}
            onClick={() => onToggle?.(value)}
            disabled={disabled}
            aria-pressed={active}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

export function DrawerForm({ onSubmit, submitting, children, className }) {
  return (
    <form
      className={`drawer-form${className ? ' ' + className : ''}`}
      onSubmit={(e) => {
        e.preventDefault();
        if (!submitting) onSubmit?.(e);
      }}
      noValidate
    >
      {children}
    </form>
  );
}

export function DrawerFormActions({
  onCancel,
  submitLabel = 'Save',
  cancelLabel = 'Cancel',
  submitting,
  submitTone = 'primary',
  destructive,
  extraLeft,
}) {
  return (
    <div className="drawer-form-actions">
      <div>{extraLeft}</div>
      <div className="drawer-form-actions-right">
        {onCancel ? (
          <button type="button" className="btn ghost" onClick={onCancel} disabled={submitting}>
            {cancelLabel}
          </button>
        ) : null}
        <button
          type="submit"
          className={`btn ${destructive ? 'destructive' : submitTone}`}
          disabled={submitting}
        >
          {submitting ? 'Saving…' : submitLabel}
        </button>
      </div>
    </div>
  );
}

export function Drawer({ open, onClose, title, children, side = 'right', width = 480 }) {
  return (
    <div className={`drawer-backdrop ${open ? 'open' : ''}`} onClick={onClose} aria-hidden={!open}>
      <aside
        className={`drawer ${side}`}
        style={{ width }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <header className="drawer-header">
          <h3 style={{ margin: 0 }}>{title}</h3>
          <button className="btn ghost sm" onClick={onClose}>
            Close
          </button>
        </header>
        <div className="drawer-body">{children}</div>
      </aside>
    </div>
  );
}

// ---------- helpers ----------
function healthTone(h) {
  if (typeof h === 'number') {
    if (h >= 75) return 'green';
    if (h >= 60) return 'amber';
    return 'red';
  }
  switch ((h || '').toLowerCase()) {
    case 'green':
    case 'ontrack':
    case 'on_track':
    case 'on track':
    case 'stable':
      return 'green';
    case 'amber':
    case 'atrisk':
    case 'at_risk':
    case 'at risk':
    case 'watch':
      return 'amber';
    case 'red':
    case 'critical':
    case 'blocked':
      return 'red';
    case 'blue':
    case 'planned':
      return 'blue';
    default:
      return 'grey';
  }
}
function healthLabel(h) {
  const t = healthTone(h);
  if (t === 'green') return 'On track';
  if (t === 'amber') return 'At risk';
  if (t === 'red') return 'Critical';
  if (t === 'blue') return 'Planned';
  return h || 'Unknown';
}
const STATUS_LABEL_MAP = {
  todo: 'To do',
  to_do: 'To do',
  backlog: 'Backlog',
  in_progress: 'In progress',
  inprogress: 'In progress',
  inProgress: 'In progress',
  doing: 'In progress',
  blocked: 'Blocked',
  block: 'Blocked',
  review: 'In review',
  in_review: 'In review',
  qa: 'QA',
  done: 'Done',
  complete: 'Complete',
  completed: 'Complete',
  closed: 'Closed',
  cancelled: 'Cancelled',
  canceled: 'Cancelled',
  ontrack: 'On track',
  on_track: 'On track',
  on_hold: 'On hold',
  onHold: 'On hold',
  atrisk: 'At risk',
  at_risk: 'At risk',
  atRisk: 'At risk',
  watch: 'Watch',
  critical: 'Critical',
  pending: 'Pending',
  pending_human_review: 'Pending review',
  pendingHumanReview: 'Pending review',
  committed: 'Committed',
  proposed: 'Proposed',
  draft: 'Draft',
  submitted: 'Submitted',
  approved: 'Approved',
  rejected: 'Rejected',
  passed: 'Passed',
  failed: 'Failed',
  exception: 'Exception',
  missing: 'Missing',
  securityPolicy: 'Security policy',
  architecture: 'Architecture',
  external: 'External',
  internal: 'Internal',
};

function prettyStatus(s) {
  if (s == null || s === '') return '—';
  const raw = String(s);
  const trimmed = raw.trim();
  if (STATUS_LABEL_MAP[trimmed]) return STATUS_LABEL_MAP[trimmed];
  const lower = trimmed.toLowerCase();
  if (STATUS_LABEL_MAP[lower]) return STATUS_LABEL_MAP[lower];
  // camelCase / kebab / snake → words; first letter upper, rest lower
  const spaced = trimmed
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!spaced) return raw;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}
function criterionTone(s) {
  switch (s) {
    case 'complete':
      return 'green';
    case 'pending':
      return 'blue';
    case 'blocked':
      return 'red';
    case 'exception':
      return 'amber';
    case 'missing':
      return 'grey';
    default:
      return 'grey';
  }
}
